import React, { memo, useMemo } from "react";

const VIEWBOX_WIDTH = 100;
const VIEWBOX_HEIGHT = 72;
const VIEWBOX_PADDING = 8;

function readFiniteNumber(value) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function buildPlotPoints(points = []) {
  const finitePoints = (Array.isArray(points) ? points : [])
    .map((point) => {
      const xMeters = readFiniteNumber(point?.xMeters);
      const yMeters = readFiniteNumber(point?.yMeters);

      if (xMeters == null || yMeters == null) {
        return null;
      }

      return { xMeters, yMeters };
    })
    .filter(Boolean);

  if (!finitePoints.length) {
    return [];
  }

  if (finitePoints.length === 1) {
    return [{ x: VIEWBOX_WIDTH / 2, y: VIEWBOX_HEIGHT / 2 }];
  }

  const xs = finitePoints.map((point) => point.xMeters);
  const ys = finitePoints.map((point) => point.yMeters);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const spanX = maxX - minX;
  const spanY = maxY - minY;
  const drawableWidth = VIEWBOX_WIDTH - VIEWBOX_PADDING * 2;
  const drawableHeight = VIEWBOX_HEIGHT - VIEWBOX_PADDING * 2;
  const scale =
    spanX > 0 && spanY > 0
      ? Math.min(drawableWidth / spanX, drawableHeight / spanY)
      : spanX > 0
        ? drawableWidth / spanX
        : spanY > 0
          ? drawableHeight / spanY
          : 1;
  const usedWidth = spanX * scale;
  const usedHeight = spanY * scale;
  const offsetX = (VIEWBOX_WIDTH - usedWidth) / 2;
  const offsetY = (VIEWBOX_HEIGHT - usedHeight) / 2;

  return finitePoints.map((point) => ({
    x:
      spanX > 0
        ? offsetX + (point.xMeters - minX) * scale
        : VIEWBOX_WIDTH / 2,
    y:
      spanY > 0
        ? VIEWBOX_HEIGHT - (offsetY + (point.yMeters - minY) * scale)
        : VIEWBOX_HEIGHT / 2,
  }));
}

const SixMinuteWalkRouteChart = memo(function SixMinuteWalkRouteChart({
  points = [],
  ariaLabel = "Walk route trace",
  height = 240,
  className = "",
  fillContainer = false,
}) {
  const plotPoints = useMemo(() => buildPlotPoints(points), [points]);
  const polylinePoints = useMemo(
    () =>
      plotPoints
        .map((point) => `${point.x.toFixed(2)},${point.y.toFixed(2)}`)
        .join(" "),
    [plotPoints],
  );

  if (!plotPoints.length) {
    return null;
  }

  const startPoint = plotPoints[0];
  const endPoint = plotPoints[plotPoints.length - 1];

  return (
    <div
      className={[
        "flex w-full items-center justify-center text-primary",
        fillContainer ? "h-full min-h-0" : "aspect-[4/3]",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      style={{ height }}
    >
      <svg
        role="img"
        aria-label={ariaLabel}
        viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
        preserveAspectRatio="xMidYMid meet"
        className="h-full w-full overflow-visible"
      >
        {plotPoints.length > 1 ? (
          <>
            <polyline
              points={polylinePoints}
              fill="none"
              stroke="currentColor"
              strokeOpacity="0.14"
              strokeWidth="7"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <polyline
              points={polylinePoints}
              fill="none"
              stroke="currentColor"
              strokeWidth="3.25"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </>
        ) : null}

        <circle
          cx={startPoint.x}
          cy={startPoint.y}
          r="2.75"
          fill="white"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <circle cx={endPoint.x} cy={endPoint.y} r="3.5" fill="currentColor" />
      </svg>
    </div>
  );
});

export default SixMinuteWalkRouteChart;
